import classNames from "classnames";
import React, { useCallback, useRef, useEffect } from "react";
import { useBuilderTheme } from "../../hooks/use-builder-theme";

import type { SwitchToggleSize } from "./switch-toggle-styles";
import { SwitchToggleContainer } from "./switch-toggle-styles";

interface BaseProps {
    readonly clickable?: boolean;
    readonly size: SwitchToggleSize;
    readonly className?: string;
    readonly acceptColorOverride?: string;
}

interface TernaryProps extends BaseProps {
    readonly toggled: boolean | undefined;
    readonly onChange?: (checked: boolean | undefined) => void;
    readonly optional: boolean;
}

interface Props extends BaseProps {
    readonly toggled: boolean;
    readonly onChange?: (checked: boolean) => void;
}

export const TernarySwitchToggle = (p: TernaryProps) => {
    const { toggled, onChange, size, clickable, className, acceptColorOverride, optional } = p;

    const theme = useBuilderTheme();
    const prevToggled = useRef(toggled);
    const lastDefinedState = useRef(toggled === true);

    useEffect(() => {
        if (toggled !== undefined) {
            lastDefinedState.current = toggled === true;
        }
        prevToggled.current = toggled;
    }, [toggled]);

    const onToggled = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (clickable === false) return;

            if (optional) {
                // Cycle through: false -> true -> undefined -> false
                if (toggled === false) {
                    onChange?.(true);
                } else if (toggled === true) {
                    onChange?.(undefined);
                } else {
                    onChange?.(false);
                }
            } else {
                onChange?.(!toggled);
            }
        },
        [onChange, toggled, clickable, optional]
    );

    const isEnabled = toggled === true;
    const isIndeterminate = toggled === undefined;

    const c = classNames(
        className,
        isEnabled && "enabled",
        isIndeterminate && "indeterminate",
        !(clickable ?? true) && "unclickable"
    );

    return (
        <SwitchToggleContainer
            className={c}
            data-test={"switch-tgl"}
            onClick={onToggled}
            color={acceptColorOverride ?? theme.acceptColor}
            size={size}>
            <div className="check">
                <div className="thumb" style={{
                    opacity: isIndeterminate ? 0 : 1,
                    transition: prevToggled.current === undefined && !isIndeterminate
                        ? 'opacity 0.2s ease-in-out'  // Coming from indeterminate - fade only
                        : isIndeterminate
                        ? 'opacity 0.2s ease-in-out'  // Going to indeterminate - fade only
                        : 'transform 0.2s, opacity 0.2s ease-in-out',  // Normal on/off transition
                    transform: isIndeterminate && lastDefinedState.current
                        ? (size === 'sm' ? 'translateX(10px)' : size === 'xsm' ? 'translateX(9px)' : 'translateX(14px)')
                        : undefined
                }} />
                {isIndeterminate && (
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60%',
                        height: '2px',
                        backgroundColor: '#9CA3AF',
                        borderRadius: '1px'
                    }} />
                )}
            </div>
        </SwitchToggleContainer>
    );
};

const SwitchToggle = (p: Props) => {
    return (
        <TernarySwitchToggle
            {...p}
            optional={false}
            onChange={(checked: boolean | undefined) => {
                if (checked !== undefined) {
                    p.onChange?.(checked);
                }
            }}
        />
    );
};

export const LabeledSwitchToggle = (p: Props) => {
    const { toggled, onChange, size, clickable, className } = p;

    const theme = useBuilderTheme();

    const onToggled = useCallback(
        (e: React.ChangeEvent) => {
            e.stopPropagation();
            if (clickable === false) return;
            onChange?.(!toggled);
        },
        [onChange, toggled, clickable]
    );

    const c = classNames(className, toggled && "enabled", !(clickable ?? true) && "unclickable");

    return (
        <SwitchToggleContainer as="label" className={c} data-test={"switch-tgl"} color={theme.acceptColor} size={size}>
            <div className="check">
                <div className="thumb" />
            </div>
            <input tw="absolute left-0 top-0 w-0 h-0" type="checkbox" checked={toggled} onChange={onToggled} />
        </SwitchToggleContainer>
    );
};

export default SwitchToggle;
